import { SearchBarProps } from "@omni/types/components/props/searchBar.props";
import { PageTemplate } from "@omni/types/enums/pageTemplate.enum";
import { setLayer } from "@omni/ui/utils/layer";
import { hasWindow } from "@omni/utils/dom.utils";
import { ReactNode, useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import { SearchBarContextProvider } from "@/components/Search/SearchBar/context/searchBar.context";
import { useAutocompleteApi } from "@/components/Search/SearchBar/hooks/useAutocompleteApi.hook";
import { AutocompleteRef } from "@/components/Search/SearchBar/types/searchBar.types";
import { getQuery } from "@/redux/modules/value/search/search.selectors";
import { blurActiveElement } from "@/utils/blur";
import { KeyNames } from "@/utils/enums";
import { useScrollLock } from "@/utils/hooks/useScrollLock";
import { createKeyHandler } from "@/utils/keyboard";
import { isPageTemplate } from "@/utils/pageTemplate.utils";
import { parseUrl } from "@/utils/search/routing/configurationFromUrl";

const AUTOCOMPLETE_SEARCH_BOX_SCROLL_LOCK = "autocomplete-search-box";

type Props = SearchBarProps & {
    children: ReactNode;
};

export const Base = ({ initialProducts, children }: Props) => {
    const { lock, unlock } = useScrollLock(AUTOCOMPLETE_SEARCH_BOX_SCROLL_LOCK);

    const isSearchPage = isPageTemplate(PageTemplate.Search);
    const initialQuery =
        useSelector(getQuery) || (isSearchPage && hasWindow())
            ? parseUrl().q ||
              ((
                  document.getElementById(
                      "autocomplete-input"
                  ) as HTMLInputElement | null
              )?.value ??
                  "")
            : "";

    const autocompleteRef = useRef<AutocompleteRef>({
        form: null,
        input: null,
        query: initialQuery
    });
    const panelRef = useRef<HTMLDivElement>(null);

    const {
        autocomplete,
        isOpen,
        open,
        close,
        autocompleteActions,
        autocompleteState,
        items
    } = useAutocompleteApi({
        autocompleteRef: autocompleteRef,
        initialProducts: initialProducts
    });

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        autocompleteRef.current.query = initialQuery;
        autocomplete.setQuery(initialQuery);
    }, [initialQuery]);

    const props = {
        root: autocomplete.getRootProps({
            "aria-labelledby": "submit-search-button"
        }),
        panel: autocomplete.getPanelProps({})
    };

    const handleOnClose = () => {
        close();
        blurActiveElement();
    };

    const handleOnKeyDown = createKeyHandler([
        {
            keys: [KeyNames.ESCAPE],
            handler: handleOnClose
        },
        {
            keys: [KeyNames.SPACE],
            handler: (event) => {
                if (isOpen) return;

                event.preventDefault();
                open();
            }
        },
        {
            defaultExcept: [KeyNames.TAB, KeyNames.SHIFT],
            handler: open
        }
    ]);

    useEffect(() => {
        const abortController = new AbortController();

        window.addEventListener(
            "keydown",
            (event) => {
                if (
                    (event.metaKey || event.ctrlKey) &&
                    event.key.toLowerCase() === KeyNames.K
                ) {
                    open();
                }
            },
            {
                signal: abortController.signal
            }
        );

        return () => {
            abortController.abort();
        };
    }, [open]);

    useEffect(() => {
        if (
            !autocompleteRef.current.form ||
            !autocompleteRef.current.input ||
            !panelRef.current
        ) {
            return;
        }

        const abortController = new AbortController();

        const { onTouchStart, onTouchMove, onMouseDown } =
            autocomplete.getEnvironmentProps({
                formElement: autocompleteRef.current.form,
                inputElement: autocompleteRef.current.input,
                panelElement: panelRef.current
            });

        window.addEventListener("mousedown", onMouseDown, {
            signal: abortController.signal
        });
        window.addEventListener("touchstart", onTouchStart, {
            signal: abortController.signal
        });
        window.addEventListener("touchmove", onTouchMove, {
            signal: abortController.signal
        });

        return () => {
            abortController.abort();
        };
    }, [autocomplete.getEnvironmentProps]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        (isOpen ? lock : unlock)();

        const navigationTopBarElement = document.querySelector(
            ".header__top-bar"
        ) as HTMLElement | null;

        if (navigationTopBarElement) {
            setLayer(navigationTopBarElement, "navigation", !isOpen);
        }
    }, [isOpen]);

    return (
        <SearchBarContextProvider
            isOpen={isOpen}
            items={items}
            props={props}
            autocomplete={autocomplete}
            autocompleteRef={autocompleteRef}
            autocompleteActions={autocompleteActions}
            autocompleteState={autocompleteState}
            onKeyDown={handleOnKeyDown}
            onOpen={open}
            onClose={handleOnClose}
        >
            {children}
        </SearchBarContextProvider>
    );
};
